/**
 * Autore: Salvo de Luca
 * Azienda: Dialogicom s.r.l.s.
 * File: src/components/modular-form/fields/MarcaTurbinaField.tsx
 * Release: v1.0.0
 * Descrizione: Campo per selezione marca turbina con dropdown filtrato per tipo mezzo
 */

import React, { useState, useEffect } from 'react';

interface MarcaTurbinaFieldProps {
  value: string;
  onChange: (value: string) => void;
  tipoMezzo?: string;
  disabled?: boolean;
  placeholder?: string;
}

// Marche turbina per tipo mezzo
const turbinaMarche = {
  auto: [
    'Garrett',
    'BorgWarner',
    'IHI',
    'Mitsubishi',
    'KKK',
    'Holset',
    'Turbo Technology',
    'Schwitzer',
    'Toyota CT',
    'Volkswagen'
  ],
  camion: [
    'Holset',
    'BorgWarner',
    'Garrett',
    'IHI',
    'Mitsubishi',
    'KKK',
    'Turbo Technology',
    'Schwitzer',
    'MAN',
    'Scania'
  ],
  furgone: [
    'Garrett',
    'BorgWarner',
    'IHI',
    'Mitsubishi',
    'KKK',
    'Holset',
    'Turbo Technology',
    'Schwitzer'
  ],
  barca: [
    'Garrett',
    'BorgWarner',
    'IHI',
    'Mitsubishi',
    'Holset',
    'Turbo Technology',
    'Yanmar',
    'Caterpillar',
    'Volvo Penta'
  ],
  trattore: [
    'Garrett',
    'BorgWarner',
    'Holset',
    'IHI',
    'Mitsubishi',
    'KKK',
    'Turbo Technology',
    'John Deere',
    'Case IH',
    'New Holland'
  ],
  moto: [
    'IHI',
    'Mitsubishi',
    'Garrett',
    'BorgWarner',
    'Kawasaki',
    'Suzuki'
  ],
  altro: [
    'Garrett',
    'BorgWarner',
    'IHI',
    'Mitsubishi',
    'KKK',
    'Holset',
    'Turbo Technology',
    'Schwitzer'
  ]
};

export const MarcaTurbinaField: React.FC<MarcaTurbinaFieldProps> = ({
  value,
  onChange,
  tipoMezzo = 'auto',
  disabled = false,
  placeholder = 'Seleziona marca turbina...'
}) => {
  const [showCustomInput, setShowCustomInput] = useState(false);
  const [customValue, setCustomValue] = useState('');

  const marche = turbinaMarche[tipoMezzo as keyof typeof turbinaMarche] || turbinaMarche.auto;

  useEffect(() => {
    // Se il valore attuale non è nelle opzioni, mostra l'input personalizzato
    if (value && !marche.includes(value)) {
      setShowCustomInput(true);
      setCustomValue(value);
    }
  }, [value, marche]);

  const handleSelectChange = (selectedValue: string) => {
    if (selectedValue === '__custom__') {
      setShowCustomInput(true);
      setCustomValue('');
      onChange('');
    } else {
      setShowCustomInput(false);
      setCustomValue('');
      onChange(selectedValue);
    }
  };

  const handleCustomInputChange = (inputValue: string) => {
    const capitalizedValue = inputValue.charAt(0).toUpperCase() + inputValue.slice(1).toLowerCase();
    setCustomValue(capitalizedValue);
    onChange(capitalizedValue);
  };

  const baseClasses = "w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 bg-zinc-700 text-white placeholder-zinc-400 border-zinc-600 transition-colors";

  if (showCustomInput) {
    return (
      <div className="space-y-2">
        <div className="flex items-center space-x-2">
          <input
            type="text"
            value={customValue}
            onChange={(e) => handleCustomInputChange(e.target.value)}
            placeholder="Inserisci marca personalizzata..."
            disabled={disabled}
            className={`${baseClasses} ${disabled ? 'opacity-50 cursor-not-allowed' : ''}`}
          />
          <button
            type="button"
            onClick={() => {
              setShowCustomInput(false);
              setCustomValue('');
              onChange('');
            }}
            disabled={disabled}
            className="px-3 py-2 text-sm bg-slate-600 hover:bg-slate-500 border border-slate-500 rounded-lg text-slate-200 hover:text-white disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
          >
            Lista
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-2">
      <select
        value={value}
        onChange={(e) => handleSelectChange(e.target.value)}
        disabled={disabled}
        className={`${baseClasses} ${disabled ? 'opacity-50 cursor-not-allowed' : ''}`}
      >
        <option value="">{placeholder}</option>
        {marche.map((marca) => (
          <option key={marca} value={marca}>
            {marca}
          </option>
        ))}
        <option value="__custom__">✏️ Inserimento manuale</option>
      </select>
    </div>
  );
};